package com.exura.pages.planCreator.components

import androidx.compose.runtime.*
import com.exura.components.snackbar.SnackbarMessage
import com.exura.features.plandefinition.domain.builder.plan.BasePlanBuilderNew
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.domain.model.series.SeriesType
import com.exura.features.plandefinition.usecase.CreateTreatmentPlanUseCase
import com.exura.features.plandefinition.usecase.CreateUnversionedPlanDefinitionUseCase
import com.exura.features.plandefinition.usecase.UpdateTreatmentPlanUseCase
import com.exura.navigation.Route
import com.exura.navigation.navigateTo
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.launch

enum class DisplayMode {
    TEXT, GRAPHICS
}

enum class ViewMode {
    VIEW, EDIT
}

fun createIndexedSeries(planBuilder: BasePlanBuilderNew<out BasePlan>): List<IndexedSeries> {
    val result = mutableListOf<IndexedSeries>()

    planBuilder.collections().forEachIndexed { collectionIndex, collection ->
        collection.series.forEachIndexed { seriesIndex, series ->
            result.add(
                IndexedSeries(
                    series = series,
                    collectionIndex = collectionIndex,
                    seriesIndex = seriesIndex,
                    collection = collection
                )
            )
        }
    }

    planBuilder.directSeries().forEachIndexed { seriesIndex, series ->
        result.add(
            IndexedSeries(
                series = series,
                collectionIndex = null,
                seriesIndex = seriesIndex,
                collection = null
            )
        )
    }

    return result
}

fun createFlattenedItems(indexedSeries: List<IndexedSeries>, groupingMode: SeriesGroupingMode): List<FlattenedItem> {
    return when (groupingMode) {
        SeriesGroupingMode.UNGROUPED -> {
            indexedSeries.map { FlattenedItem.SeriesItem(it) }
        }

        SeriesGroupingMode.GROUPED -> {
            val items = mutableListOf<FlattenedItem>()
            val collectionsUsed = mutableSetOf<Int>()

            indexedSeries.forEach { series ->
                val collectionIndex = series.collectionIndex
                if (collectionIndex != null) {
                    if (collectionIndex !in collectionsUsed) {
                        items.add(
                            FlattenedItem.CollectionHeader(
                                collection = series.collection!!,
                                collectionIndex = collectionIndex
                            )
                        )
                        collectionsUsed.add(collectionIndex)
                    }
                }
                items.add(FlattenedItem.SeriesItem(series))
            }

            items
        }
    }
}

fun updateUrlForSelectedSeries(indexedSeries: IndexedSeries?) {
    val params = if (indexedSeries != null) {
        val (collectionIndex, seriesIndex) = indexedSeries.toUrlIdentifier()
        buildMap<String, String> {
            collectionIndex?.let { put("collectionIndex", it.toString()) }
            put("seriesIndex", seriesIndex.toString())
        }
    } else {
        mapOf("seriesIndex" to null, "collectionIndex" to null)
    }
}

fun onPlanSave(
    coroutineScope: CoroutineScope,
    planBuilder: BasePlanBuilderNew<out BasePlan>,
    sourceId: String?,
    createTreatmentPlanUseCase: CreateTreatmentPlanUseCase,
    updateTreatmentPlanUseCase: UpdateTreatmentPlanUseCase,
    onFailure: () -> Unit,
    onNewIdAssigned: (newId: String?) -> Unit,
): Unit {
    coroutineScope.launch {
        val plan = planBuilder.rebuild()

        val result = if (sourceId == null) {
            createTreatmentPlanUseCase(plan)
        } else {
            updateTreatmentPlanUseCase(plan, sourceId)
        }

        result.onSuccess { savedPlan ->
            val newId = savedPlan.firebaseDocId
            planBuilder.setFirebaseDocId(newId)
            planBuilder.markSaved()
            onNewIdAssigned(newId)
        }.onFailure {
            println(it)
            println(it.message)
            onFailure()
        }
    }
}

fun onPlanSend(
    coroutineScope: CoroutineScope,
    planBuilder: BasePlanBuilderNew<out BasePlan>,
    sourceId: String?,
    onNewPlan: () -> Unit,
    onChangedPlan: () -> Unit,
    onSend: (planId: String) -> Unit,
): Unit {
    coroutineScope.launch {
        val plan = planBuilder.rebuild()

        val isAdjusted = planBuilder.isChanged

        if (sourceId != null) {
            if (!isAdjusted) {
                onSend(sourceId)
            } else {
                onChangedPlan()
            }
        } else {
            onNewPlan()
        }
    }
}

@Composable
internal fun PlanCanvasContainer(
    pageContext: PageContext,
    coroutineScope: CoroutineScope,
    planBuilder: BasePlanBuilderNew<out BasePlan>,
    snackbarEmitter: Channel<SnackbarMessage>,
    viewMode: ViewMode,
    onViewModeChange: (ViewMode) -> Unit,
    displayMode: DisplayMode,
    onDisplayModeChange: (DisplayMode) -> Unit,
    sourceId: String?,
    selectedSeriesIndex: Int?,
    selectedCollectionIndex: Int?,
    onIndexChange: (Int?, Int?) -> Unit,
    onSourceIdChange: (String?) -> Unit,
) {
    val createTreatmentPlanUseCase = injectAndRemember<CreateTreatmentPlanUseCase>()
    val updateTreatmentPlanUseCase = injectAndRemember<UpdateTreatmentPlanUseCase>()

    var seriesGroupingMode by remember { mutableStateOf(SeriesGroupingMode.GROUPED) }

    var flattenedIndexedSeries by remember(planBuilder) { mutableStateOf(emptyList<IndexedSeries>()) }
    var flattenedItems by remember(flattenedIndexedSeries, seriesGroupingMode) {
        mutableStateOf(createFlattenedItems(flattenedIndexedSeries, seriesGroupingMode))
    }

    var selectedSeriesTypeForCreation by remember { mutableStateOf<SeriesType?>(null) }
    var selectedIndexedSeries by remember(planBuilder) { mutableStateOf<IndexedSeries?>(null) }

    var isChanged by remember { mutableStateOf(planBuilder.isChanged) }
    var isValid by remember { mutableStateOf(planBuilder.validate().isEmpty()) }

    var title by remember(planBuilder) { mutableStateOf(planBuilder.title() ?: "") }
    var description by remember(planBuilder) { mutableStateOf(planBuilder.description() ?: "") }

    fun recalculateFlattened() {
        flattenedIndexedSeries = createIndexedSeries(planBuilder)
        flattenedItems = createFlattenedItems(flattenedIndexedSeries, seriesGroupingMode)
    }

    fun updateChanges() {
        isChanged = planBuilder.isChanged
        val validationMessages = planBuilder.validate()
        isValid = validationMessages.isEmpty()
    }

    val onPlanSaveClick: () -> Unit = {
        onPlanSave(
            coroutineScope = coroutineScope,
            planBuilder = planBuilder,
            sourceId = sourceId,
            createTreatmentPlanUseCase = createTreatmentPlanUseCase,
            updateTreatmentPlanUseCase = updateTreatmentPlanUseCase,
            onFailure = { snackbarEmitter.trySend(SnackbarMessage("Błąd podczas zapisywania planu")) },
        ) { newId ->
            onSourceIdChange(newId)
            updateChanges()
        }
    }

    val onPlanSendClick: () -> Unit = {
        onPlanSend(
            coroutineScope = coroutineScope,
            planBuilder = planBuilder,
            sourceId = sourceId,
            onNewPlan = { snackbarEmitter.trySend(SnackbarMessage("Nie można wysłać planu, który nie został dodany do biblioteki planów")) },
            onChangedPlan = { snackbarEmitter.trySend(SnackbarMessage("Nie można wysłać planu z niezapisanymi zmianami")) },
        ) {
            pageContext.navigateTo(
                Route.WithParams.toPlanDistribution(planId = it)
            )
        }
    }

    LaunchedEffect(planBuilder, seriesGroupingMode) {
        recalculateFlattened()
        selectedIndexedSeries = if (selectedSeriesIndex != null) {
            IndexedSeries.fromUrlIdentifier(
                collectionIndex = selectedCollectionIndex,
                seriesIndex = selectedSeriesIndex,
                flattenedSeries = flattenedIndexedSeries
            )
        } else {
            null
        }
    }

    LaunchedEffect(selectedIndexedSeries) {
        onIndexChange(selectedIndexedSeries?.collectionIndex, selectedIndexedSeries?.seriesIndex)
    }

    Column(
        modifier = Modifier.fillMaxSize().overflow(Overflow.Auto)
    ) {
        // Canvas Top Bar
        CanvasTopBar(
            title = title,
            description = description,
            planBuilder = planBuilder,
            displayMode = displayMode,
            onDisplayModeChange = onDisplayModeChange,
            onTitleChange = { new ->
                planBuilder.setTitle(new)
                title = new
                updateChanges()
            },
            onDescriptionChange = { new ->
                planBuilder.setDescription(new)
                description = new
                updateChanges()
            },
            viewMode = viewMode,
            onViewModeChange = onViewModeChange,
            onActionClick = if (viewMode == ViewMode.EDIT) onPlanSaveClick else onPlanSendClick,
            isChanged = isChanged,
            isValid = isValid,
        )

        // Canvas Content based on display mode
        Box(
            modifier = Modifier.fillMaxWidth().weight(1f).overflow(Overflow.Auto)
        ) {
            when (displayMode) {
                DisplayMode.TEXT -> TextPlanView(
                    viewMode = viewMode,
                    planType = planBuilder.planType,
                    flattenedItems = flattenedItems,
                    selectedIndexedSeries = selectedIndexedSeries,
                    onSeriesSelected = { series ->
                        selectedSeriesTypeForCreation = null
                        selectedIndexedSeries = series
                    },
                    selectedSeriesTypeForCreation = selectedSeriesTypeForCreation,
                    onSeriesTypeForCreationSelected = { seriesType ->
                        selectedIndexedSeries = null
                        selectedSeriesTypeForCreation = seriesType
                    },
                    onSeriesSaved = { series ->
                        selectedIndexedSeries?.let { currentSelection ->
                            val colId = currentSelection.collectionIndex
                            val seriesId = currentSelection.seriesIndex
                            if (colId != null) {
                                planBuilder.editCollection(colId) {
                                    val newSeries = it.series.toMutableList()
                                    newSeries[seriesId] = series
                                    it.copy(series = newSeries)
                                }
                            } else {
                                planBuilder.updateDirectSeries(seriesId, series)
                            }
                            recalculateFlattened()
                            selectedIndexedSeries = flattenedIndexedSeries.find { indexedSeries ->
                                (indexedSeries.collectionIndex == colId) &&
                                (indexedSeries.seriesIndex == seriesId)
                            }
                            updateChanges()
                        }

                    },
                    onSeriesAdded = { series ->
                        planBuilder.addDirectSeries(series)
                        recalculateFlattened()

                        selectedIndexedSeries = flattenedIndexedSeries.find {
                            (it.series == series) && (it.collectionIndex == null)
                        }
                        selectedSeriesTypeForCreation = null
                        updateChanges()
                    },
                    seriesGroupingMode = seriesGroupingMode,
                    onSeriesGroupingModeChange = { seriesGroupingMode = it },
                    snackbarEmitter = snackbarEmitter,
                )

                DisplayMode.GRAPHICS -> {
                    // TODO: Graphics view implementation
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText("Graphics View - Coming Soon")
                    }
                }
            }
        }
    }
}
